'use client';

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Callout, Spinner, TextButton } from '@galapagos/design-system/web';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { sponsorRewardsOn } from '@/services/link-incentive';
import { StepStatusRegion } from '../../components/StepStatusRegion';
import {
  directDownloadCampaign,
  directDownloadPlacement,
  directDownloadPlatform,
  directDownloadStoreUrl,
  isDirectDownload,
} from '../../download/direct-download';
import { downloadVersion } from '../../download/download-version';
import { ensureDownloadStyle } from '../../download/download-style';
import { useSignupApi } from '../../hooks/useSignupApi';
import { useActiveSignupSponsor } from '../../hooks/useActiveSignupSponsor';
import { useFinishSignupFlow } from '../../hooks/useFinishSignupFlow';
import { useLinkIncentive } from '../../hooks/useLinkIncentive';
import { useLegacySignupParams } from '../../hooks/useLegacySignupParams';
import { useStepHeading } from '../../hooks/useStepHeading';
import { useSignupFlowStore } from '../../stores/signup-flow-store';
import { gated } from '../wrap-up/gated';
import { WrapUpFrame } from '../wrap-up/WrapUpFrame';
import { DownloadQrCard } from './DownloadQrCard';
import { RatingSummary } from './RatingSummary';
import { ReviewList } from './ReviewList';
import { StoreBadges } from './StoreBadges';

/**
 * `/download/`: the end of the flow. It hands the member to the app
 * with store badges, plus a QR on a desktop-width screen. Members whose
 * employer has rewards and who chose chat see the rewards version. There is
 * nothing after it, so it offers no navigation.
 */
function DownloadStep() {
  const { t } = useTranslation('signup');
  ensureDownloadStyle();
  const api = useSignupApi();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const scenario = searchParams.get('scenario');
  const { download } = useLegacySignupParams();
  const directDownload = isDirectDownload(download);
  // The link's sponsor, or on a link without one the employer picked in search.
  const sponsor = useActiveSignupSponsor();
  const chatChosen = useSignupFlowStore((store) => store.chatChosen);
  // Signup ends here: the answers kept for this tab go, and this page keeps
  // reading the in-memory choice it needs.
  useFinishSignupFlow();
  // The links carry the landing campaign, which the root capture saves in its
  // own effect on this same first render. Reading only after this page has
  // mounted means every effect of that render, the capture included, has run.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  // The device is only known in the browser, and the links wait for mount.
  const platform = mounted ? directDownloadPlatform(window.navigator.userAgent) : 'other';
  // The direct-download link labels its links by device and carries its own
  // campaign, so an earlier landing's UTMs never label it.
  const request = useMemo(
    () =>
      directDownload
        ? { placement: directDownloadPlacement(platform), campaign: directDownloadCampaign(search) }
        : undefined,
    [directDownload, platform, search],
  );
  const handoff = useQuery({
    queryKey: ['signup', 'download-handoff', scenario, request],
    queryFn: () => api.getDownloadHandoff(request),
    retry: false,
    enabled: mounted,
  });

  // Only a member who chose chat can see the rewards version, so only they
  // wait for the sponsor read; a failed read means the default page.
  const versionPending = chatChosen && sponsor.isPending;
  const incentive = useLinkIncentive();
  const version = downloadVersion({
    rewards: sponsorRewardsOn(sponsor.data?.features.rewards, incentive),
    chatChosen,
  });
  const rewards = version === 'rewards';
  const title = rewards ? t('download.rewardsTitle') : t('download.title');
  const headingRef = useStepHeading(title);

  const loading = handoff.isPending || versionPending;

  // On a phone the link also opens that phone's store, once the badges are on
  // screen: the badges stay the path that always works, the jump is a bonus.
  const storeUrl =
    directDownload && handoff.data && !loading
      ? directDownloadStoreUrl(platform, handoff.data)
      : null;
  const jumped = useRef(false);
  useEffect(() => {
    if (storeUrl === null || jumped.current) return;
    jumped.current = true;
    window.location.replace(storeUrl);
  }, [storeUrl]);
  // A failure is announced by its error callout's alert, not this status line.
  const status = loading ? t('download.loading') : '';

  return (
    <WrapUpFrame
      title={title}
      description={rewards ? undefined : t('download.description')}
      headingRef={headingRef}
    >
      <StepStatusRegion message={status} />
      <div className="signup-download" data-testid="signup-download">
        {rewards && !loading ? (
          <Callout
            title={t('download.rewards.title')}
            body={t('download.rewards.body')}
            data-testid="signup-download-rewards"
          />
        ) : null}
        {loading ? <Spinner accessibilityLabel={t('download.loading')} /> : null}
        {handoff.isError && !loading ? (
          <Callout
            tone="error"
            body={t('download.loadError')}
            data-testid="signup-download-error"
            action={
              <TextButton
                label={t('download.retry')}
                actionId="signup.download-retry.activate"
                data-testid="signup-download-retry-button"
                onPress={() => void handoff.refetch()}
              />
            }
          />
        ) : null}
        {handoff.data && !loading ? (
          <>
            <DownloadQrCard scanUrl={handoff.data.scanUrl} />
            <StoreBadges handoff={handoff.data} />
          </>
        ) : null}
        {!rewards && !loading ? (
          <>
            <RatingSummary />
            <ReviewList />
          </>
        ) : null}
      </div>
    </WrapUpFrame>
  );
}

export const DownloadScreen = gated(DownloadStep);
